import styled from '@emotion/styled';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Menu as SemanticMenu, MenuItemProps, Sidebar as SemanticSidebar } from 'semantic-ui-react';
import { IntlDateTimeFormatFormat } from '../Intl/Intl.DateTimeFormat.prototype.format';
import { IntlDateTimeFormatFormatRange } from '../Intl/Intl.DateTimeFormat.prototype.formatRange';
import { IntlDateTimeFormatFormatRangeToParts } from '../Intl/Intl.DateTimeFormat.prototype.formatRangeToParts';
import { IntlDateTimeFormatFormatToParts } from '../Intl/Intl.DateTimeFormat.prototype.formatToParts';
import { IntlDateTimeFormatResolvedOptions } from '../Intl/Intl.DateTimeFormat.prototype.resolvedOptions';
import { IntlListFormatFormat } from '../Intl/Intl.ListFormat.prototype.format';
import { IntlListFormatFormatToParts } from '../Intl/Intl.ListFormat.prototype.formatToParts';
import { DateToLocaleString } from '../Intl/Date.prototype.toLocaleString';
import { DateToLocaleTimeString } from '../Intl/Date.prototype.toLocaleTimeString';
import { DateToLocaleDateString } from '../Intl/Date.prototype.toLocaleDateString';
import { IntlRelativeTimeFormatFormat } from '../Intl/Intl.RelativeTimeFormat.prototype.format';
import { IntlRelativeTimeFormatFormatToParts } from '../Intl/Intl.RelativeTimeFormat.prototype.formatToParts';
import { IntlRelativeTimeFormatResolvedOptions } from '../Intl/Intl.RelativeTimeFormat.prototype.resolvedOptions';
import { IntlNumberFormatFormat } from '../Intl/Intl.NumberFormat.prototype.format';
import { IntlNumberFormatFormatToParts } from '../Intl/Intl.NumberFormat.prototype.formatToParts';
import { IntlNumberFormatResolvedOptions } from '../Intl/Intl.NumberFormat.prototype.resolvedOptions';
import { NumberToLocaleString } from '../Intl/Number.prototype.toLocaleString';
import { IntlCollatorCompare } from '../Intl/Intl.Collator.prototype.compare';
import { ArraySort } from '../Intl/Array.prototype.sort';
import { IntlCollatorResolvedOptions } from '../Intl/Intl.Collator.prototype.resolvedOptions';
import { StringLocaleCompare } from '../Intl/String.prototype.localeCompare';
import { IntlPluralRulesSelect } from '../Intl/Intl.PluralRules.prototype.select';
import { IntlPluralRulesResolvedOptions } from '../Intl/Intl.PluralRules.prototype.resolvedOptions';
import { IntlDisplayNamesOf } from '../Intl/Intl.DisplayNames.prototype.of';
import { IntlDisplayNamesResolvedOptions } from '../Intl/Intl.DisplayNames.prototype.resolvedOptions';
import { MenuItems } from '../Domain/MenuItem';

export type Props = {
	isVisible: boolean;

	onHide: () => void;
	onSelect: (element: JSX.Element) => void;
};

const menuFeed: Record<string, Record<string, JSX.Element | null>> = {
	'Formatting date: DateTimeFormat': {
		'Intl.DateTimeFormat.prototype.format': <IntlDateTimeFormatFormat />,
		'Intl.DateTimeFormat.prototype.formatToParts': <IntlDateTimeFormatFormatToParts />,
		'Intl.DateTimeFormat.prototype.formatRange': <IntlDateTimeFormatFormatRange />,
		'Intl.DateTimeFormat.prototype.formatRangeToParts': (
			<IntlDateTimeFormatFormatRangeToParts />
		),
		'Intl.DateTimeFormat.prototype.resolvedOptions': <IntlDateTimeFormatResolvedOptions />
	},
	'Formatting date: Date': {
		'Date.prototype.toLocaleString': <DateToLocaleString />,
		'Date.prototype.toLocaleDateString': <DateToLocaleDateString />,
		'Date.prototype.toLocaleTimeString': <DateToLocaleTimeString />
	},
	'Relative time format': {
		'Intl.RelativeTimeFormat.prototype.format': <IntlRelativeTimeFormatFormat />,
		'Intl.RelativeTimeFormat.prototype.formatToParts': <IntlRelativeTimeFormatFormatToParts />,
		'Intl.RelativeTimeFormat.prototype.resolvedOptions': (
			<IntlRelativeTimeFormatResolvedOptions />
		)
	},
	'Formatting numbers: NumberFormat': {
		'Intl.NumberFormat.prototype.format': <IntlNumberFormatFormat />,
		'Intl.NumberFormat.prototype.formatToParts': <IntlNumberFormatFormatToParts />,
		'Intl.NumberFormat.prototype.resolvedOptions': <IntlNumberFormatResolvedOptions />
	},
	'Formatting numbers: Number': {
		'Number.prototype.toLocaleString': <NumberToLocaleString />
	},
	'String collation: Collator': {
		'Intl.Collator.prototype.compare': <IntlCollatorCompare />,
		'Intl.Collator.prototype.resolvedOptions': <IntlCollatorResolvedOptions />
	},
	'String collation: Array': {
		'Array.prototype.sort': <ArraySort />
	},
	'String collation: String': {
		'String.prototype.localeCompare': <StringLocaleCompare />
	},
	'List format': {
		'Intl.ListFormat.prototype.format': <IntlListFormatFormat />,
		'Intl.ListFormat.prototype.formatToParts': <IntlListFormatFormatToParts />
	},
	'Display names': {
		'Intl.DisplayNames.prototype.of': <IntlDisplayNamesOf />,
		'Intl.DisplayNames.prototype.resolvedOptions': <IntlDisplayNamesResolvedOptions />
	},
	'Plural rules': {
		'Intl.PluralRules.prototype.select': <IntlPluralRulesSelect />,
		'Intl.PluralRules.prototype.resolvedOptions': <IntlPluralRulesResolvedOptions />
	}
};

const pathMapping = Object.values(menuFeed).reduce(
	(agg, curr) => ({ ...agg, ...curr }),
	{} as Record<string, JSX.Element | null>
);

const Sidebar = styled(SemanticSidebar)``;

export const Menu: React.FC<Props> = ({ isVisible, onHide, onSelect }) => {
	const history = useHistory();
	const location = useLocation();

	
	const functionInPath = location.pathname.substring(1) as string;

	const [active, setActive] = useState<string>(
		functionInPath ?? 'Intl.DateTimeFormat.prototype.format'
	);
	const selectedComponent = pathMapping[functionInPath];

	const menuItems = new MenuItems(Object.values(menuFeed).flatMap(Object.entries), active);

	useEffect(() => {
		if (selectedComponent) {
			onSelect(selectedComponent);
		}
	}, [selectedComponent, onSelect]);

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if ((e.target as HTMLElement).tagName === "INPUT") {
				return;
			}

			if (e.key === 'ArrowLeft') {
				selectComponent(...menuItems.loopBack());
			}

			if (e.key === 'ArrowRight') {
				selectComponent(...menuItems.loopForward());
			}
		};

		document.addEventListener('keydown', handler);

		return () => {
			document.removeEventListener('keydown', handler);
		};
	});

	const selectComponent = (name: string,component: JSX.Element) => {
		history.push(name);
		onSelect(component);
		setActive(name);
	};

	const handleItemClick = (component: JSX.Element | null) => (
		_: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
		{ name }: MenuItemProps
	) => {
		if (component && name) {
			selectComponent(name, component);
			onHide();
		}
	};

	return (
		<Sidebar
			as={SemanticMenu}
			animation="overlay"
			vertical
			visible={isVisible}
			onHide={onHide}
			size="large"
			width="very wide"
		>
			{Object.entries(menuFeed).map(([header, items]) => (
				<SemanticMenu.Item key={header}>
					<SemanticMenu.Header>{header}</SemanticMenu.Header>
					<SemanticMenu.Menu>
						{Object.entries(items).map(([fnName, component]) => (
							<SemanticMenu.Item
								name={fnName}
								key={fnName}
								active={active === fnName}
								disabled={component === null}
								onClick={handleItemClick(component)}
							>
								{fnName}
							</SemanticMenu.Item>
						))}
					</SemanticMenu.Menu>
				</SemanticMenu.Item>
			))}
		</Sidebar>
	);
};
